import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import DownloadLink from "./DownloadLink";
import { imprimerDocument } from "../services/imprimer";
import { qlAlert } from "../utils/dialogs";
import MathText from "./MathText";

// Lecteur PDF intégré (pdf.js), chargé seulement quand un PDF est ouvert.
const PdfCanvasPreview = lazy(() => import("../teacher/components/PdfCanvasPreview"));

const IMAGE = /\.(jpe?g|png|gif|webp|bmp|avif)(\?|$)/i;
const SITE = (process.env.REACT_APP_WEB_BASE_URL || "https://www.quicklearning-cmr.com").replace(/\/$/, "");

// Le lecteur intégré doit recevoir une adresse complète, joignable depuis Internet.
export function adresseComplete(url) {
  if (!url || /^(https?:|blob:|data:)/i.test(url)) return url;
  if (typeof window !== "undefined" && /^https?:/.test(window.location.origin) && !/localhost|127\.0\.0\.1/.test(window.location.origin)) {
    return new URL(url, window.location.origin).toString();
  }
  return `${SITE}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function typeDocument(url) {
  return IMAGE.test(url || "") ? "image" : "pdf";
}

// Visionneuse plein écran (PDF, image ou texte avec formules), lisible aussi sur téléphone.
// Elle est affichée directement sur la page (portail) : ouverte depuis une conversation, elle ne
// reste plus enfermée dans le panneau glissant du téléphone, où sa barre « Fermer » pouvait être
// masquée et bloquer l'élève. Le geste « retour » du téléphone la ferme aussi.
export default function DocumentViewer({ titre, url: adresse, texte, telechargement = true, onClose }) {
  // La messagerie se rafraîchit toutes les 4 s et le serveur renvoie à chaque fois un
  // nouveau lien signé pour le même fichier : on garde le premier, sinon le PDF
  // recommence à se charger indéfiniment.
  const adresseFigee = useRef(adresse);
  const sansSignature = (u) => String(u || "").split("?")[0];
  if (sansSignature(adresse) !== sansSignature(adresseFigee.current)) adresseFigee.current = adresse;
  const url = adresseComplete(adresseFigee.current);
  const [repli, setRepli] = useState(false);
  const fermerRef = useRef(onClose);
  fermerRef.current = onClose;
  useEffect(() => {
    const fermer = (event) => { if (event.key === "Escape") fermerRef.current(); };
    document.addEventListener("keydown", fermer);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", fermer);
      document.body.style.overflow = overflow;
    };
  }, []);
  useEffect(() => {
    // Une entrée d'historique par visionneuse ouverte (l'état existant est conservé).
    let fermeeParRetour = false;
    window.history.pushState({ ...(window.history.state || {}), qlVisionneuse: true }, "");
    const surRetour = () => { fermeeParRetour = true; fermerRef.current(); };
    window.addEventListener("popstate", surRetour);
    return () => {
      window.removeEventListener("popstate", surRetour);
      if (!fermeeParRetour && window.history.state?.qlVisionneuse) window.history.back();
    };
  }, []);

  const type = texte ? "texte" : typeDocument(url);
  const nomFichier = `${(titre || "document").replace(/[\\/:*?"<>|]+/g, " ").trim()}.${type === "image" ? "jpg" : "pdf"}`;

  const visionneuse = (
    <div className="ql-docviewer" role="dialog" aria-modal="true" aria-label={titre}>
      <div className="ql-docviewer-bar">
        <span className="ql-docviewer-title">{titre}</span>
        <div className="ql-docviewer-actions">
          {url && telechargement && (
            <DownloadLink url={url} filename={nomFichier} mimeType={type === "image" ? "image/jpeg" : "application/pdf"} className="btn btn-sm btn-light"
              imprimable={false}>
              <i className="bi bi-download me-1"></i>Télécharger
            </DownloadLink>
          )}
          {url && type !== "texte" && (
            <button type="button" className="btn btn-sm btn-light" onClick={() => imprimerDocument({ url, filename: nomFichier,
              mimeType: type === "image" ? "image/jpeg" : "application/pdf" }).catch(() => qlAlert("Impression impossible.", "error"))}>
              <i className="bi bi-printer me-1"></i>Imprimer
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline-light ql-docviewer-fermer" onClick={onClose} aria-label="Fermer">
            <i className="bi bi-x-lg me-1"></i>Fermer
          </button>
        </div>
      </div>
      <div className="ql-docviewer-body">
        {type === "texte" && <div className="ql-docviewer-text"><MathText text={texte} /></div>}
        {type === "image" && <img src={url} alt={titre} className="ql-docviewer-image" />}
        {/* PDF lu directement par l'application (pages affichées une à une, sans service extérieur) ;
            le lecteur en ligne de Google ne sert plus que de solution de repli. */}
        {type === "pdf" && !repli && (
          <Suspense fallback={<div className="ql-pdf-canvas-state ql-docviewer-pdf">Chargement du PDF…</div>}>
            <PdfCanvasPreview url={url} title={titre} className="ql-docviewer-pdf" onErreur={() => setRepli(true)} />
          </Suspense>
        )}
        {type === "pdf" && repli && (
          <iframe
            className="ql-docviewer-frame"
            title={titre}
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
          />
        )}
      </div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(visionneuse, document.body) : visionneuse;
}
